/**
 * Reusable date-range preset dropdown.
 *
 * Popover trigger that shows the active range or preset name. Inside the
 * popover: list of presets + optional custom from/to date inputs +
 * "Use today for end date" toggle. Designed as a drop-in for any feature
 * that needs a compact, consistent date picker.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import {
  formatDateDMY,
  todayIsoDateParis,
} from '@/features/smswiser-campaigns/utils/dateFormat';

import {
  getPresetFromRange,
  getRangeFromPreset,
  getStatsDateRangeLabel,
  STATS_DATE_RANGE_PRESETS,
} from './dateRangePresets';
import type { StatsDateRangePreset } from './dateRangePresets';

interface DateRangePresetDropdownProps {
  fromDate: string;
  toDate: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  /** When provided, called for preset changes — updates both dates atomically to avoid stale data. */
  onDateRangeChange?: (from: string, to: string) => void;
  /**
   * Preset-first callback. When provided it takes precedence over onDateRangeChange
   * for preset picks, handing the parent the exact preset code. Needed for rolling
   * presets (last-24-hours / last-48-hours) whose {from,to} would otherwise collapse
   * to another preset when round-tripped through getPresetFromRange.
   */
  onPresetChange?: (preset: StatsDateRangePreset) => void;
  /** Explicit active preset for the label + highlight (overrides the {from,to} reverse lookup). */
  activePreset?: StatsDateRangePreset;
  /** When true, show "Use today for end date" checkbox in custom section */
  showToDateIsToday?: boolean;
  toDateIsToday?: boolean;
  onToDateIsTodayChange?: (v: boolean) => void;
  /** Presets not offered (e.g. hour rules: no rolling 24/48-hour windows); a range matching one reads as custom dates. */
  excludePresets?: readonly string[];
}

export const DateRangePresetDropdown: FC<DateRangePresetDropdownProps> = ({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onDateRangeChange,
  onPresetChange,
  activePreset,
  showToDateIsToday = false,
  toDateIsToday = false,
  onToDateIsTodayChange,
  excludePresets,
}) => {
  const [open, setOpen] = useState(false);
  const [localFrom, setLocalFrom] = useState(fromDate);
  const [localTo, setLocalTo] = useState(toDate);
  const [localToDateIsToday, setLocalToDateIsToday] = useState(toDateIsToday);

  useEffect(() => {
    if (open) {
      setLocalFrom(fromDate);
      setLocalTo(toDateIsToday ? todayIsoDateParis() : toDate);
      setLocalToDateIsToday(toDateIsToday);
    }
  }, [open, fromDate, toDate, toDateIsToday]);

  const preset = useMemo(() => {
    const p =
      activePreset ??
      (toDateIsToday ? 'custom' : getPresetFromRange(fromDate, toDate));
    return excludePresets?.includes(p) ? 'custom' : p;
  }, [activePreset, fromDate, toDate, toDateIsToday, excludePresets]);

  const display = useMemo(() => {
    if (!fromDate && !toDate && !toDateIsToday) {
      return 'Select date range';
    }
    if (!fromDate || (!toDate && !toDateIsToday)) {
      return 'Select date range';
    }
    if (preset !== 'custom') return getStatsDateRangeLabel(preset);
    if (toDateIsToday) {
      return `${formatDateDMY(fromDate)} → Today`;
    }
    return `${formatDateDMY(fromDate)} → ${formatDateDMY(toDate)}`;
  }, [preset, fromDate, toDate, toDateIsToday]);

  const handlePickPreset = useCallback(
    (p: StatsDateRangePreset) => {
      if (p === 'custom') return;
      const range = getRangeFromPreset(p);
      if (range) {
        // Preset-first: preserves the exact preset (e.g. last-24-hours) instead of
        // collapsing it via the {from,to} round-trip. Falls back to the legacy
        // {from,to} callbacks for consumers that don't track the preset.
        if (onPresetChange) {
          onPresetChange(p);
        } else if (onDateRangeChange) {
          onDateRangeChange(range.from, range.to);
        } else {
          onFromChange(range.from);
          onToChange(range.to);
        }
        onToDateIsTodayChange?.(false);
        setOpen(false);
      }
    },
    [
      onFromChange,
      onToChange,
      onDateRangeChange,
      onPresetChange,
      onToDateIsTodayChange,
    ],
  );

  const handleApply = useCallback(() => {
    onFromChange(localFrom);
    if (localToDateIsToday) {
      onToChange(todayIsoDateParis());
      onToDateIsTodayChange?.(true);
    } else {
      onToChange(localTo);
      onToDateIsTodayChange?.(false);
    }
    setOpen(false);
  }, [
    localFrom,
    localTo,
    localToDateIsToday,
    onFromChange,
    onToChange,
    onToDateIsTodayChange,
  ]);

  const presets = STATS_DATE_RANGE_PRESETS.filter(
    (p) => p.code !== 'custom' && !excludePresets?.includes(p.code),
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex w-fit min-w-0 items-center justify-between gap-1.5"
          data-testid="date-range-preset-trigger"
        >
          <span
            className={cn(
              'whitespace-nowrap',
              !fromDate && !toDate && !toDateIsToday && 'text-muted-foreground',
            )}
          >
            {display}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            // z-[250] (not z-50): the tag-rule modals portal to <body> at
            // z-[200], which painted this popover behind them. Matches
            // CreatableCombobox, which sits above those modals the same way.
            'z-[250] w-max min-w-[140px] rounded-md border border-border bg-popover py-1.5 text-popover-foreground shadow-md',
          )}
        >
          <div className="max-h-[220px] overflow-y-auto px-1">
            {presets.map((p) => {
              const active = preset === p.code;
              return (
                <button
                  key={p.code}
                  type="button"
                  className={cn(
                    'flex w-full items-center rounded px-2 py-1.5 text-left text-xs transition-colors',
                    active ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50',
                  )}
                  onClick={() => handlePickPreset(p.code)}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          <div className="my-1 border-t border-border" />

          <div className="px-2 pb-1.5">
            <div className="mb-1 flex flex-col gap-1">
              <input
                type="date"
                value={localFrom}
                onChange={(e) => setLocalFrom(e.target.value)}
                className="h-7 w-full min-w-0 rounded border border-input bg-background px-2 text-xs"
              />
              <input
                type="date"
                value={localToDateIsToday ? todayIsoDateParis() : localTo}
                onChange={(e) => setLocalTo(e.target.value)}
                disabled={localToDateIsToday}
                className="h-7 w-full min-w-0 rounded border border-input bg-background px-2 text-xs disabled:opacity-60"
              />
            </div>
            {showToDateIsToday && (
              <label className="mb-1.5 flex cursor-pointer items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={localToDateIsToday}
                  onChange={(e) => setLocalToDateIsToday(e.target.checked)}
                  className="rounded border-input"
                />
                <span>Use today for end date</span>
              </label>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full text-xs"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
